import React, { Component, Fragment } from 'react';
import { withApollo } from 'react-apollo';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { Formik, Field, FastField } from 'formik';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _includes from 'lodash/includes';
import Grid from '@material-ui/core/Grid';
import GridItem from './../../components/Global/GridItem';
import RadioField from '../../components/Global/Inputs/RadioField';
import PhoneField from './../../components/Global/Inputs/PhoneField';
import PropTypes from 'prop-types';

import Button from '@material-ui/core/Button';
import Autocomplete from '../../components/Global/Inputs/Autocomplete';
import ProgressButton from '../../components/Global/Buttons/Progress';
import Divider from '@material-ui/core/Divider';
import LoadingIndicator from '../../components/LoadingIndicator';
import ErrorNotification from '../../components/common/ErrorNotification';
import TextFieldInput from '../../components/Global/Inputs/TextField';
import OutlinedSelectInput from '../../components/Global/Inputs/OutlinedSelect';
import CheckboxInput from '../../components/Global/Inputs/Checkbox';
import TimeFieldInput from '../../components/Global/Inputs/TimeField';
import OutlinedSelectChipped from '../../components/Global/Inputs/OutlinedSelectChipped';
import { DATATYPES } from '../../constants/constants';
import { COUNTRIES, STATES } from '../../constants/dropdowns';

import combineStyles from '../../utils/combineStyles';
import LayoutStyles from '../../styles/layout';
import ButtonStyles from '../../styles/button';
import TypographyStyles from '../../styles/typography';
import SpacingStyles from '../../styles/helpers/spacing';

import OrganizationModel, { sanitize } from '../../Models/Organization';
import OrganizationSchema from './Organization.schema';

import * as Queries from '../../graphQL/queries/orgQueries';
import * as Mutations from '../../graphQL/mutations/orgMutations';
import { locationsByOrgIdQuery } from '../../graphQL/queries/locationQueries';
import { allContacts } from '../../graphQL/queries/contactQueries';

import OrganizationFormStyles from './OrganizationForm.styles';

import { AppContext } from '../../AppProvider';
import GlobalProps from '../../GlobalProps/GlobalProps';

import {
  DEFAULT_BRANDING_ID,
  DEFAULT_ORGANIZATION_TYPE_ID,
  DEFAULT_TIMEZONE,
  DEFAULT_COUNTRY,
  DEFAULT_RATE_PROVIDER_ID,
  DEFAULT_UNIT_TYPE
} from '../../constants/constants';

export class OrganizationForm extends Component {
  static propTypes = {
    location: PropTypes.object.isRequired
  };
  state = {
    error: null,
    org: {},
    locations: [],
    contacts: [],
    referenceFields: {},
    brandingOptions: [],
    organizationTypes: [],
    formSchema: {},
    errors: null,
    isSubmitting: false,
    createNewOrg: false,
    orgIdToEdit: null,
    onGeneralPage: false,
    loading: true
  };

  async componentDidMount() {
    this.setState({ ...OrganizationModel() });
    const { t } = this.props;
    const { defaultCountry } = this.context.globalProps.orgInfo.config;
    const formSchema = OrganizationSchema({ t });

    this.setState({ defaultCountry, formSchema });
    try {
      this._getRefData();

      const orgIdToEdit = this._parseLocation();
      if (orgIdToEdit) {
        const locations = await this._getOrgLocations(orgIdToEdit);
        const contacts = await this._getOrgContacts(orgIdToEdit);
        const org = await this._getOrganization(orgIdToEdit);

        this.setState({
          locations,
          contacts,
          org
        });
      }
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  }

  _parseLocation = () => {
    const { isPropelAdministrator, currentOrgId } = this.context.globalProps;
    const {
      location,
      match: {
        params: { id }
      }
    } = this.props;

    const orgIdToEdit = id;

    if (_includes(location.pathname, 'general')) {
      this.setState({ orgIdToEdit: currentOrgId, onGeneralPage: true });
      return currentOrgId;
    }
    if (_includes(location.pathname, 'edit') && isPropelAdministrator) {
      this.setState({
        orgIdToEdit,
        createNewOrg: false
      });
      return id;
    }
    if (_includes(location.pathname, 'new') && isPropelAdministrator) {
      this.setState({
        org: { ...OrganizationModel() },
        orgIdToEdit: null,
        createNewOrg: true
      });
    }
    return null;
  };

  _getOrganization = async orgIdToEdit => {
    try {
      const { client: ApolloClient } = this.props;

      const {
        data: {
          organization: { byId: org }
        }
      } = await ApolloClient.query({
        query: Queries.byId,
        variables: { id: orgIdToEdit }
      });
      return org;
    } catch (error) {
      this.setState({ error });
      return Promise.reject(error);
    }
  };

  _getRefData = () => {
    const {
      timeZones,
      unitTypes,
      cultures,
      country,
      currencies,
      rateProviders
    } = this.context.globalProps.referenceData;
    const { brandingOptions, organizationTypes } = this.context.globalProps;

    this.setState({
      referenceFields: {
        timeZones,
        unitTypes,
        cultures,
        country,
        currencies,
        rateProviders
      },
      brandingOptions,
      organizationTypes
    });
  };

  _getOrgLocations = async orgIdToEdit => {
    try {
      const { client: ApolloClient } = this.props;

      const {
        data: {
          location: { all: locations }
        }
      } = await ApolloClient.query({
        query: locationsByOrgIdQuery,
        variables: {
          orgId: orgIdToEdit
        }
      });
      return locations;
    } catch (error) {
      this.setState({ error });
      return Promise.reject(error);
    }
  };

  _getOrgContacts = async orgIdToEdit => {
    try {
      const { client: ApolloClient } = this.props;

      const {
        data: {
          contact: { all: contacts }
        }
      } = await ApolloClient.query({
        query: allContacts,
        variables: {
          orgId: orgIdToEdit
        }
      });
      return contacts;
    } catch (error) {
      this.setState({ error });
      return Promise.reject(error);
    }
  };

  _handleOnSave = async organizationValues => {
    const { client: ApolloClient, history } = this.props;
    const { createNewOrg } = this.state;
    try {
      this.setState({ isSubmitting: true });

      if (createNewOrg) {
        await ApolloClient.mutate({
          mutation: Mutations.createOrgMutation,
          variables: { organization: sanitize(organizationValues) }
        });
        history.push('/admin/organizations');
      } else {
        const {
          data: {
            organization: { update: org }
          }
        } = await ApolloClient.mutate({
          mutation: Mutations.updateOrgMutation,
          variables: { organization: sanitize(organizationValues) }
        });
        this.setState({
          org
        });
        this.context.resyncGlobalProps();
      }
    } catch (error) {
      this.setState({
        errors: error.message
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  _handleChange = (field, value) => {
    this.setState({ [field]: value });
  };

  _handleCancel = () => {
    const { history } = this.props;
    const { onGeneralPage } = this.state;

    if (!onGeneralPage) {
      history.push('/admin/organizations');
    }
  };

  _getLocationSuggestions = async value => {
    const { locations } = this.state;
    const suggestionResults = locations.filter(location => {
      const regex = new RegExp(value, 'i');
      return regex.test(location.title);
    });
    const suggestions = suggestionResults.map(result => {
      return {
        label: result.title,
        ...result
      };
    });
    return Promise.resolve(suggestions);
  };

  _getContactSuggestions = async value => {
    const { contacts } = this.state;
    const regex = new RegExp('^' + value, 'i');
    const suggestionResults = contacts.filter(contact => {
      return regex.test(contact.title);
    });

    const suggestions = suggestionResults.map(result => {
      return {
        label: result.title,
        ...result
      };
    });
    return Promise.resolve(suggestions);
  };

  _handleValidateField = async (
    currentField,
    currentValue,
    values,
    setFieldError
  ) => {
    const { formSchema } = this.state;
    const valueCopy = { ...values };
    const updatedValues = _set(valueCopy, currentField, currentValue);

    const defaultPaymentTerms = _get(
      values,
      'config.defaultBillingTerms.defaultPaymentTerms',
      'Thisisinvalid'
    );
    const isThirdPartyBilling = defaultPaymentTerms === 'THIRD_PARTY';

    try {
      await formSchema.validateAt(currentField, updatedValues, {
        context: { isThirdPartyBilling }
      });
      setFieldError(currentField, null);
    } catch (err) {
      setFieldError(currentField, err.message);
    }
  };

  _handleAccessOrganization = async () => {
    const { org } = this.state;
    const { globalProps } = this.context;

    try {
      const GP = new GlobalProps();
      const updatedGlobalProps = await GP.setupOrganization(
        org.id,
        globalProps
      );
      this.context.updateGlobalProps(updatedGlobalProps);
      this.props.history.push('/dashboard');
    } catch (error) {
      this.setState({ error });
    }
  };

  render() {
    const { t, classes } = this.props;
    const {
      referenceFields,
      locations,
      org,
      brandingOptions,
      organizationTypes,
      createNewOrg,
      onGeneralPage
    } = this.state;
    const { isPropelAdministrator, currentOrgId } = this.context.globalProps;

    if (this.state.loading) {
      return <LoadingIndicator />;
    }

    if (this.state.error) {
      return <ErrorNotification message="Error loading page" />;
    }

    return (
      <div>
        <Formik
          validationSchema={OrganizationSchema({ t })}
          enableReinitialize={true}
          validateOnChange={false}
          validateOnBlur={false}
          initialValues={{
            ...org
          }}
          onSubmit={async values => {
            this._handleOnSave(values);
          }}
          onReset={(values, actions) => {
            this._handleCancel();
            actions.resetForm();
          }}
          render={({
            values,
            handleChange,
            setFieldError,
            setFieldValue,
            handleSubmit,
            handleReset,
            dirty
          }) => (
            <Fragment>
              <div className={classes.titleWrapper}>
                <h1
                  className={classnames(
                    classes.headline4,
                    classes.noMarginBottom,
                    classes.title
                  )}
                >
                  {createNewOrg ? t('Add organization') : t('General Settings')}
                </h1>
                <div className={classes.actionsWrapper}>
                  {values.id &&
                    isPropelAdministrator &&
                    currentOrgId !== values.id && (
                      <Button
                        className={classes.button}
                        classes={{
                          label: classes.buttonLabel
                        }}
                        variant="contained"
                        color="primary"
                        onClick={this._handleAccessOrganization}
                      >
                        {t('Access')}
                      </Button>
                    )}
                  <Button
                    color={'primary'}
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    classes={{
                      label: classes.buttonLabel
                    }}
                    onClick={handleReset}
                  >
                    {onGeneralPage ? t('Reset') : t('Cancel')}
                  </Button>
                  <ProgressButton
                    color={'primary'}
                    className={classnames(
                      classes.button,
                      classes.actionButtons
                    )}
                    classes={{
                      label: classes.buttonLabel,
                      disabled: classnames(
                        classes.buttonDisabled,
                        classes.actionButtonDisabled
                      )
                    }}
                    disabled={!dirty}
                    onClick={handleSubmit}
                    isSubmitting={this.state.isSubmitting}
                  >
                    {createNewOrg ? t('Add Organization') : t('Save Settings')}
                  </ProgressButton>
                </div>
              </div>
              <Divider className={classes.divider} />

              <div className={classnames(classes.wrapper, classes.formWrapped)}>
                <Grid container spacing={16}>
                  <GridItem xs={12}>
                    <div>
                      <p
                        className={classnames(
                          classes.headline7,
                          classes.sectionTitle
                        )}
                      >
                        {t('Configuration')}
                      </p>
                    </div>
                  </GridItem>
                  <GridItem lg={6}>
                    <Field
                      name={'name'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            label={t('Organization Name')}
                            value={values.name}
                            fullwidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <Field
                      name="externalId"
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(values, 'externalId', '')}
                            label={t('External Id')}
                            fullWidth
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>

                  <GridItem xs={6}>
                    <FastField
                      name={'timeZoneId'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('Time Zone')}
                            value={_get(values, 'timeZoneId', DEFAULT_TIMEZONE)}
                            options={referenceFields.timeZones}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={'config.defaultUnitType'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('Unit Type')}
                            value={_get(
                              values,
                              'config.defaultUnitType',
                              DEFAULT_UNIT_TYPE
                            )}
                            options={referenceFields.unitTypes}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                </Grid>
              </div>
              {isPropelAdministrator && (
                <div
                  className={classnames(classes.wrapper, classes.formWrapped)}
                >
                  <Grid container spacing={16}>
                    <GridItem xs={12}>
                      <div>
                        <p
                          className={classnames(
                            classes.headline7,
                            classes.sectionTitle
                          )}
                        >
                          {t('Customer Service Only')}
                        </p>
                      </div>
                    </GridItem>
                    {!values.id && (
                      <div
                        className={classnames(
                          classes.wrapper,
                          classes.formWrapped
                        )}
                      >
                        <Grid container spacing={16}>
                          <GridItem xs={12}>
                            <div>
                              <p
                                className={classnames(
                                  classes.headline7,
                                  classes.sectionTitle
                                )}
                              >
                                {t('Initial User')}
                              </p>
                            </div>
                          </GridItem>
                          <GridItem xs={6}>
                            <FastField
                              name={'superUserFirstName'}
                              onChange={handleChange}
                              render={({ field, form }) => {
                                return (
                                  <TextFieldInput
                                    value={_get(
                                      values,
                                      'superUserFirstName',
                                      ''
                                    )}
                                    label={t('First Name')}
                                    fullWidth
                                    field={field}
                                    form={form}
                                  />
                                );
                              }}
                            />
                          </GridItem>
                          <GridItem xs={6}>
                            <FastField
                              name={'superUserLastName'}
                              onChange={handleChange}
                              render={({ field, form }) => {
                                return (
                                  <TextFieldInput
                                    value={_get(
                                      values,
                                      'superUserLastName',
                                      ''
                                    )}
                                    label={t('Last Name')}
                                    fullWidth
                                    field={field}
                                    form={form}
                                  />
                                );
                              }}
                            />
                          </GridItem>
                          <GridItem xs={12}>
                            <FastField
                              name={'superUserLoginEmail'}
                              onChange={handleChange}
                              render={({ field, form }) => {
                                return (
                                  <TextFieldInput
                                    value={_get(
                                      values,
                                      'superUserLoginEmail',
                                      ''
                                    )}
                                    label={t('Email')}
                                    fullWidth
                                    field={field}
                                    form={form}
                                  />
                                );
                              }}
                            />
                          </GridItem>
                        </Grid>
                      </div>
                    )}
                    <GridItem xs={4}>
                      <FastField
                        name={'config.brandingId'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <OutlinedSelectInput
                              label={t('Branding')}
                              value={_get(
                                values,
                                'config.brandingId',
                                DEFAULT_BRANDING_ID
                              )}
                              options={brandingOptions.map(b => ({
                                value: b.id,
                                title: b.name
                              }))}
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'config.organizationTypeId'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <OutlinedSelectInput
                              label={t('Organization Type')}
                              value={_get(
                                values,
                                'config.organizationTypeId',
                                DEFAULT_ORGANIZATION_TYPE_ID
                              )}
                              options={organizationTypes.map(ot => ({
                                value: ot.enumValue,
                                title: ot.name
                              }))}
                              field={field}
                              form={form}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                     
                      <FastField
                        name={'config.rateProviders'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <OutlinedSelectChipped
                              classes={{ root: classes.noMarginBottom }}
                              value={_get(values, 'config.rateProviders', [
                                DEFAULT_RATE_PROVIDER_ID
                              ]).filter(x =>
                                referenceFields.rateProviders.find(
                                  r => r.value === x
                                )
                              )}
                              field={field}
                              form={form}
                              label={t('Rate Providers')}
                              options={referenceFields.rateProviders}
                              multiple={true}
                              withError={true}
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'config.p44AccountGroup'}
                        onChange={handleChange}
                        render={({ field, form }) => {
                          return (
                            <TextFieldInput
                              value={_get(values, 'config.p44AccountGroup', '')}
                              label={t('P44 Account Group')}
                              field={field}
                              form={form}
                              fullWidth
                            />
                          );
                        }}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'config.orgUsersMustAcceptEula'}
                        onChange={handleChange}
                        render={({ field, form }) => (
                          <CheckboxInput
                            label={t('Org Users Must Accept Eula')}
                            field={field}
                            form={form}
                            value={_get(
                              values,
                              'config.orgUsersMustAcceptEula',
                              true
                            )}
                            classes={{
                              root: classnames(classes.noMarginTop)
                            }}
                          />
                        )}
                      />
                    </GridItem>
                    <GridItem xs={4}>
                      <FastField
                        name={'config.autoTenderEnabled'}
                        onChange={handleChange}
                        render={({ field, form }) => (
                          <CheckboxInput
                            label={t('Auto Tender Enabled')}
                            field={field}
                            form={form}
                            value={_get(
                              values,
                              'config.autoTenderEnabled',
                              false
                            )}
                            classes={{
                              root: classnames(classes.noMarginTop)
                            }}
                          />
                        )}
                      />
                    </GridItem>
                  </Grid>
                </div>
              )}
              <div className={classnames(classes.wrapper, classes.formWrapped)}>
                <Grid container spacing={16}>
                  <GridItem xs={12}>
                    <div>
                      <p
                        className={classnames(
                          classes.headline7,
                          classes.sectionTitle
                        )}
                      >
                        {t('Shipment Settings')}
                      </p>
                    </div>
                  </GridItem>
                  <GridItem xs={4}>
                    <FastField
                      name={'config.defaultDockOpenTime'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TimeFieldInput
                            label={t('Default Dock Open')}
                            value={_get(
                              values,
                              'config.defaultDockOpenTime',
                              28800
                            )}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={4}>
                    <FastField
                      name={'config.defaultDockCloseTime'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TimeFieldInput
                            label={t('Default Dock Close')}
                            value={_get(
                              values,
                              'config.defaultDockCloseTime',
                              57600
                            )}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>

                  <GridItem xs={4}>
                    <FastField
                      name={'config.defaultLeadTimeWindowMinutes'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.leadTimeWindowsMinutes',
                              120
                            )}
                            label={t('Tender Lead Time (Minutes)')}
                            field={field}
                            form={form}
                            type="number"
                            min={1}
                            datatype={DATATYPES.NUMBER}
                          />
                        );
                      }}
                    />
                  </GridItem>

                  {!!values.id && (
                    <Fragment>
                      <GridItem xs={6}>
                        <FastField
                          name={'config.defaultLocationId'}
                          onChange={handleChange}
                          render={({ field, form }) => {
                            return (
                              <OutlinedSelectInput
                                label={t('Default Location')}
                                value={_get(
                                  values,
                                  'config.defaultLocationId',
                                  null
                                )}
                                options={locations.map(l => ({
                                  value: l.id,
                                  title: l.name
                                }))}
                                field={field}
                                form={form}
                              />
                            );
                          }}
                        />
                      </GridItem>
                    </Fragment>
                  )}
                  <GridItem xs={6}>
                    <FastField
                      name={'config.defaultCountry'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <OutlinedSelectInput
                            label={t('Country')}
                            value={_get(
                              values,
                              'config.defaultCountry',
                              DEFAULT_COUNTRY
                            )}
                            options={referenceFields.country}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={'config.autoGeneratedBolNumbers'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t('Auto Generate BOL Numbers')}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.autoGeneratedBolNumbers',
                            true
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={12}>
                    <FastField
                      name={'config.defaultBolInstructions'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.defaultBolInstructions',
                              ''
                            )}
                            classes={{ root: classes.noMarginBottom }}
                            field={field}
                            fullWidth
                            form={form}
                            label={t('Default BOL Instructions')}
                            multiline={true}
                            withError={'true'}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <div
                    className={classnames(classes.wrapper, classes.formWrapped)}
                  >
                    <Grid container spacing={16}>
                      <GridItem xs={12}>
                        <div>
                          <p
                            className={classnames(
                              classes.headline7,
                              classes.sectionTitle
                            )}
                          >
                            {t('Default Billing Terms')}
                          </p>
                        </div>
                      </GridItem>
                      <Field
                        name={'config.defaultBillingTerms.defaultPaymentTerms'}
                        render={({ field, form }) => {
                          return (
                            <RadioField
                              options={[
                                {
                                  value: 'SET_BASED_ON_DIRECTION',
                                  label: t('Set Based On Direction')
                                },
                                {
                                  value: 'THIRD_PARTY',
                                  label: t('Third Party')
                                }
                              ]}
                              onChange={handleChange}
                              field={field}
                              form={form}
                              value={_get(
                                values,
                                'config.defaultBillingTerms.defaultPaymentTerms',
                                'SET_BASED_ON_DIRECTION'
                              )}
                            />
                          );
                        }}
                      />
                      <Grid
                        container
                        spacing={16}
                        classes={{
                          container: classes.formContainer
                        }}
                      >
                        <Grid
                          item
                          lg={12}
                          classes={{ item: classes.subheadingTitle }}
                        >
                          <p
                            className={classnames(
                              classes.locationDetails,
                              classes.headline7
                            )}
                          >
                            {t('Default Third-Party Billing Information')}
                          </p>
                        </Grid>
                        <GridItem lg={12}>
                          <Field
                            name={'config.defaultBillingTerms.companyName'}
                            render={({ field, form }) => {
                              return (
                                <Autocomplete
                                  value={
                                    _get(
                                      values,
                                      'config.defaultBillingTerms.companyName',
                                      ''
                                    ) || ''
                                  }
                                  getSuggestions={this._getLocationSuggestions}
                                  classes={{ root: classes.locations }}
                                  getSuggestionValue={suggestion => {
                                    return suggestion.label;
                                  }}
                                  onSuggestionSelected={location => {
                                    this._handleLocationSelected(
                                      location,
                                      setFieldValue
                                    );
                                  }}
                                  label={t('Company Name')}
                                  validateField={(
                                    currentField,
                                    currentValue
                                  ) => {
                                    this._handleValidateField(
                                      currentField,
                                      currentValue,
                                      values,
                                      setFieldError
                                    );
                                  }}
                                  onChange={change => {
                                    this._handleChange(
                                      'config.defaultBillingTerms.companyName',
                                      change
                                    );
                                    setFieldValue(
                                      'config.defaultBillingTerms.companyName',
                                      change
                                    );
                                  }}
                                  field={field}
                                  form={form}
                                />
                              );
                            }}
                          />
                        </GridItem>
                        <GridItem lg={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.addressInfo.address1',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.addressInfo.address1'
                            }
                            label={t('Address')}
                            onChange={handleChange}
                            component={TextFieldInput}
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                            fullWidth
                          />
                        </GridItem>
                        <GridItem lg={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.addressInfo.address2',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.addressInfo.address2'
                            }
                            label={t('Address 2')}
                            onChange={handleChange}
                            component={TextFieldInput}
                          />
                        </GridItem>
                        <GridItem lg={6} md={6}>
                          <Field
                            name={
                              'config.defaultBillingTerms.addressInfo.country'
                            }
                            onChange={handleChange}
                            render={({ field, form }) => {
                              return (
                                <OutlinedSelectInput
                                  value={
                                    _get(
                                      values,
                                      'config.defaultBillingTerms.addressInfo.country',
                                      DEFAULT_COUNTRY
                                    ) || DEFAULT_COUNTRY
                                  }
                                  field={field}
                                  form={form}
                                  label={t('Country')}
                                  options={COUNTRIES}
                                  multiple={false}
                                  classes={{}}
                                  validateField={(
                                    currentField,
                                    currentValue
                                  ) => {
                                    this._handleValidateField(
                                      currentField,
                                      currentValue,
                                      values,
                                      setFieldError
                                    );
                                  }}
                                />
                              );
                            }}
                          />
                        </GridItem>
                        <GridItem lg={6} md={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.addressInfo.city',
                              ''
                            )}
                            name={'config.defaultBillingTerms.addressInfo.city'}
                            label={t('City')}
                            onChange={handleChange}
                            component={TextFieldInput}
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                          />
                        </GridItem>
                        <GridItem lg={6} md={6}>
                          <Field
                            name={
                              'config.defaultBillingTerms.addressInfo.state'
                            }
                            onChange={handleChange}
                            render={({ field, form }) => {
                              return (
                                <OutlinedSelectInput
                                  value={_get(
                                    values,
                                    'config.defaultBillingTerms.addressInfo.state',
                                    ''
                                  )}
                                  field={field}
                                  form={form}
                                  label={t('State/Province')}
                                  options={
                                    STATES[
                                      _get(
                                        values,
                                        'config.defaultBillingTerms.addressInfo.country',
                                        DEFAULT_COUNTRY
                                      ) || DEFAULT_COUNTRY
                                    ]
                                  }
                                  multiple={false}
                                  classes={{
                                    root: classes.dropdownsMargin
                                  }}
                                  validateField={(
                                    currentField,
                                    currentValue
                                  ) => {
                                    this._handleValidateField(
                                      currentField,
                                      currentValue,
                                      values,
                                      setFieldError
                                    );
                                  }}
                                />
                              );
                            }}
                          />
                        </GridItem>
                        <GridItem lg={6} md={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.addressInfo.postalCode',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.addressInfo.postalCode'
                            }
                            label={t('Zip Code')}
                            onChange={handleChange}
                            component={TextFieldInput}
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                          />
                        </GridItem>
                        <Grid
                          item
                          lg={12}
                          classes={{ item: classes.subheadingTitle }}
                        >
                          <p className={classnames(classes.headline7)}>
                            {t('Contact Details')}
                          </p>
                        </Grid>
                        <GridItem lg={6}>
                          <Field
                            name={
                              'config.defaultBillingTerms.contactInfo.firstName'
                            }
                            render={({ field, form }) => {
                              return (
                                <Autocomplete
                                  value={
                                    _get(
                                      values,
                                      'config.defaultBillingTerms.contactInfo.firstName',
                                      ''
                                    ) || ''
                                  }
                                  validateField={(
                                    currentField,
                                    currentValue
                                  ) => {
                                    this._handleValidateField(
                                      currentField,
                                      currentValue,
                                      values,
                                      setFieldError
                                    );
                                  }}
                                  getSuggestions={this._getContactSuggestions}
                                  getSuggestionValue={suggestion => {
                                    return suggestion.label;
                                  }}
                                  onSuggestionSelected={contact => {
                                    this._handleContactSelected(
                                      contact,
                                      setFieldValue
                                    );
                                  }}
                                  label={t('First Name')}
                                  onChange={change => {
                                    this._handleChange(
                                      'config.defaultBillingTerms.contactInfo.firstName',
                                      change
                                    );
                                    setFieldValue(
                                      'config.defaultBillingTerms.contactInfo.firstName',
                                      change
                                    );
                                  }}
                                  field={field}
                                  form={form}
                                  fullWidth
                                />
                              );
                            }}
                          />
                        </GridItem>
                        <GridItem lg={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.contactInfo.lastName',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.contactInfo.lastName'
                            }
                            label={t('Last Name')}
                            onChange={handleChange}
                            component={TextFieldInput}
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                            fullWidth
                          />
                        </GridItem>
                        <GridItem lg={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.contactInfo.phone',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.contactInfo.phone'
                            }
                            label={t('Phone')}
                            onChange={handleChange}
                            component={PhoneField}
                            country={
                              _get(
                                values,
                                'config.defaultBillingTerms.addressInfo.country',
                                DEFAULT_COUNTRY
                              ) || DEFAULT_COUNTRY
                            }
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                            fullWidth
                          />
                        </GridItem>
                        <GridItem lg={6}>
                          <Field
                            value={_get(
                              values,
                              'config.defaultBillingTerms.contactInfo.email',
                              ''
                            )}
                            name={
                              'config.defaultBillingTerms.contactInfo.email'
                            }
                            label={t('Email')}
                            onChange={handleChange}
                            component={TextFieldInput}
                            validateField={(currentField, currentValue) => {
                              this._handleValidateField(
                                currentField,
                                currentValue,
                                values,
                                setFieldError
                              );
                            }}
                            fullWidth
                          />
                        </GridItem>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
              </div>
              <div className={classnames(classes.wrapper, classes.formWrapped)}>
                <Grid container spacing={16}>
                  <GridItem xs={12}>
                    <div>
                      <p
                        className={classnames(
                          classes.headline7,
                          classes.sectionTitle
                        )}
                      >
                        {t('Volume Rating Thresholds')}
                      </p>
                    </div>
                  </GridItem>
                  <GridItem xs={3}>
                    <FastField
                      name={'config.minimumLinearFeetForVltl'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.minimumLinearFeetForVltl',
                              12
                            )}
                            label={t('Linear Feet')}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={3}>
                    <FastField
                      name={'config.minimumPalletCountForVltl'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.minimumPalletCountForVltl',
                              6
                            )}
                            label={t('Pallet Count')}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={3}>
                    <FastField
                      name={'config.minimumWeightInPoundsForVltl'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.minimumWeightInPoundsForVltl',
                              5000
                            )}
                            label={t('Weight (Pounds)')}
                            field={field}
                            form={form}
                          />
                        );
                      }}
                    />
                  </GridItem>
                </Grid>
              </div>
              <div className={classnames(classes.wrapper, classes.formWrapped)}>
                <Grid container spacing={16}>
                  <GridItem xs={12}>
                    <div>
                      <p
                        className={classnames(
                          classes.headline7,
                          classes.sectionTitle
                        )}
                      >
                        {t('Invoice Settings')}
                      </p>
                    </div>
                  </GridItem>
                  <GridItem xs={4}>
                    <FastField
                      name={'config.requireGlCodeAssigned'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t('Require GL Code Assigned')}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.requireGlCodeAssigned',
                            false
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={4}>
                    <FastField
                      name={'config.requireProofOfDeliveryDocument'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t('Require Proof of Delivery Document')}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.requireProofOfDeliveryDocument',
                            true
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={4}>
                    <FastField
                      name={'config.requireSignedBolDocument'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t('Require Signed BOL Document')}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.requireSignedBolDocument',
                            true
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={'config.varianceAllowedAmount'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.varianceAllowedAmount',
                              10
                            )}
                            label={t('Variance Allowed Amount ($)')}
                            field={field}
                            form={form}
                            startAdornment={'$'}
                            type="number"
                            min={1}
                            datatype={DATATYPES.NUMBER}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={'config.varianceAllowedPercent'}
                      onChange={handleChange}
                      render={({ field, form }) => {
                        return (
                          <TextFieldInput
                            value={_get(
                              values,
                              'config.varianceAllowedPercent',
                              5
                            )}
                            label={t('Variance Allowed Percent')}
                            field={field}
                            form={form}
                            startAdornment={'%'}
                            type="number"
                            min={1}
                            datatype={DATATYPES.NUMBER}
                          />
                        );
                      }}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={'config.acceptVarianceInYourFavor'}
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t('Accept Variance In Your Favor')}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.acceptVarianceInYourFavor',
                            true
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                  <GridItem xs={6}>
                    <FastField
                      name={
                        'config.automaticallyAcceptInvoiceWithinVarianceThreshold'
                      }
                      onChange={handleChange}
                      render={({ field, form }) => (
                        <CheckboxInput
                          label={t(
                            'Automatically Accept Variance Within Threshold'
                          )}
                          field={field}
                          form={form}
                          value={_get(
                            values,
                            'config.automaticallyAcceptInvoiceWithinVarianceThreshold',
                            true
                          )}
                          classes={{
                            root: classnames(classes.noMarginTop)
                          }}
                        />
                      )}
                    />
                  </GridItem>
                </Grid>
              </div>
              <Divider className={classes.divider} />
              <div className={classes.actionsWrapper}>
                <Button
                  color={'primary'}
                  className={classnames(classes.button, classes.actionButtons)}
                  classes={{
                    label: classes.buttonLabel
                  }}
                  onClick={handleReset}
                >
                  {onGeneralPage ? t('Reset') : t('Cancel')}
                </Button>
                <ProgressButton
                  color={'primary'}
                  className={classnames(classes.button, classes.actionButtons)}
                  classes={{
                    label: classes.buttonLabel,
                    disabled: classnames(
                      classes.buttonDisabled,
                      classes.actionButtonDisabled
                    )
                  }}
                  disabled={!dirty}
                  onClick={handleSubmit}
                  isSubmitting={this.state.isSubmitting}
                >
                  {createNewOrg ? t('Add Organization') : t('Save Settings')}
                </ProgressButton>
              </div>
            </Fragment>
          )}
        />
        {this.state.errors && <ErrorNotification message={this.state.errors} />}
      </div>
    );
  }
}

OrganizationForm.contextType = AppContext;
const OrganizationFormWithTranslation = withNamespaces()(OrganizationForm);
export default withStyles(
  combineStyles(
    LayoutStyles,
    ButtonStyles,
    TypographyStyles,
    SpacingStyles,
    OrganizationFormStyles
  )
)(withApollo(OrganizationFormWithTranslation));
